import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { Lock, Loader2, CheckCircle, UserPlus, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import api from '@/services/api'

const setupSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

const Setup = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [invitationData, setInvitationData] = useState(null)
  const [setupComplete, setSetupComplete] = useState(false)

  const form = useForm({
    resolver: zodResolver(setupSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        const response = await api.get(`/employees/invitation/${token}`)
        if (response.data.success) {
          setInvitationData(response.data.data)
        } else {
          toast.error(response.data.error)
          navigate('/login')
        }
      } catch (error) {
        console.error('Error fetching invitation details:', error)
        toast.error('Invalid or expired invitation link')
        navigate('/login')
      } finally {
        setFetching(false)
      }
    }

    if (token) {
      fetchInvitationDetails()
    } else {
      navigate('/login')
    }
  }, [token, navigate])

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      const response = await api.post('/employees/invitation/complete', {
        token,
        password: values.password
      })

      if (response.data.success) {
        setSetupComplete(true)
        toast.success('Account setup completed successfully!')
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        toast.error(response.data.error)
      }
    } catch (error) {
      console.error('Setup completion error:', error)
      toast.error(error.response?.data?.error || 'Failed to complete setup')
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading invitation details...</p>
        </div>
      </div>
    )
  }

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-100 border border-emerald-200 mb-4">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Setup complete!
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your account has been successfully set up.
            </p>
          </div>

          <Card className="border border-border shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center mb-6">
                You will be redirected to the login page shortly.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full h-10">
                Go to Login
              </Button>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            &copy; {new Date().getFullYear()} Buzzinga EMS. All rights reserved.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 mb-4">
            <UserPlus className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Complete your setup
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome to Buzzinga EMS, {invitationData?.name?.split(' ')[0]}!
          </p>
        </div>

        {/* Account Info Card */}
        <Card className="border border-border shadow-sm mb-4">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-muted">
                <Info className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-2 text-sm flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium text-foreground">{invitationData?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium text-foreground text-xs">{invitationData?.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Employee ID</span>
                  <span className="font-medium text-foreground">{invitationData?.employee_id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Role</span>
                  <Badge variant="outline" className="capitalize">
                    {invitationData?.role}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Card */}
        <Card className="border border-border shadow-sm">
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Create Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="password"
                            placeholder="Enter your password"
                            className="pl-9 h-10"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="password"
                            placeholder="Confirm your password"
                            className="pl-9 h-10"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full h-10"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </Button>
              </form>
            </Form>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Choose a strong password that you haven't used elsewhere.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          &copy; {new Date().getFullYear()} Buzzinga EMS. All rights reserved.
        </p>
      </div>
    </div>
  )
}

export default Setup
